/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Console-styled modal shell used by the GPU Pods screens (confirmations,
// template edit, save-as-template, price comparison).

import React, { useEffect } from 'react';
import { CloseIc } from '../console/icons';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Tailwind max-width class for the panel. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Extra z-index class (defaults to z-50). */
  z?: string;
}

const sizes: Record<NonNullable<DialogProps['size']>, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const Dialog: React.FC<DialogProps> = ({ open, onClose, title, icon, children, footer, size = 'sm', z = 'z-50' }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className={`fixed inset-0 ${z} overflow-y-auto`} role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-black/70" onClick={onClose} aria-hidden="true" />
      <div className="relative flex min-h-full items-center justify-center p-4">
        <div className={`ps-card relative w-full ${sizes[size]} shadow-2xl flex flex-col max-h-[90vh]`}>
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ps-border">
            <div className="flex items-center gap-3 min-w-0">
              {icon}
              <h2 className="m-0 text-lg font-medium text-ps-text truncate">{title}</h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-10 h-10 -mr-2 grid place-items-center rounded-md text-ps-muted hover:text-ps-text hover:bg-ps-hover"
            >
              <CloseIc />
            </button>
          </div>
          <div className="px-5 py-4 overflow-y-auto flex-1">{children}</div>
          {footer && (
            <div className="px-5 py-4 border-t border-ps-border flex flex-col-reverse sm:flex-row justify-end gap-2.5">
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/** Tinted callout used inside dialogs and forms. */
export const Callout: React.FC<{
  tone?: 'warn' | 'err' | 'info' | 'off';
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}> = ({ tone = 'info', title, children, className }) => {
  const cls =
    tone === 'warn'
      ? 'bg-ps-warn-bg border-[#4A3A12] text-ps-warn'
      : tone === 'err'
        ? 'bg-ps-err-bg border-[#4A1F1F] text-ps-err'
        : tone === 'off'
          ? 'bg-ps-raised border-ps-border text-ps-muted'
          : 'bg-ps-info-bg border-[#1F3350] text-ps-info';
  return (
    <div className={`rounded-lg border px-3.5 py-3 text-[13px] leading-relaxed ${cls} ${className || ''}`}>
      {title && <p className="m-0 font-semibold">{title}</p>}
      <div className={title ? 'mt-1 opacity-90' : ''}>{children}</div>
    </div>
  );
};

export default Dialog;
